export const SORT_LINK_SELECTOR =
  "a.aqui-base-table__sort-button[href*='sort=']";
export const SKELETON_SHOWN_EVENT = "educator-home:skeleton-shown";

export function ariaSortForHref(href) {
  const url = new URL(href, window.location.origin);
  const sort =
    url.searchParams.get("sort") ||
    url.searchParams.get("early_sort") ||
    url.searchParams.get("late_sort");
  const direction =
    url.searchParams.get("direction") ||
    url.searchParams.get("early_direction") ||
    url.searchParams.get("late_direction");

  if (!sort || !direction) return "none";

  return direction === "desc" ? "descending" : "ascending";
}

export function applySortToTables(root, columnIndex, targetAriaSort) {
  root.querySelectorAll("table").forEach((table) => {
    const headers = table.querySelectorAll("th[aria-sort]");
    if (!headers[columnIndex]) return;

    headers.forEach((header, index) => {
      header.setAttribute(
        "aria-sort",
        index === columnIndex ? targetAriaSort : "none",
      );
    });
  });
}

export function captureSortSnapshot(host, root) {
  const headers = root.querySelectorAll("table th[aria-sort]");
  host.preOptimisticSortSnapshot = Array.from(headers).map((header) =>
    header.getAttribute("aria-sort"),
  );
}

export function restoreSortSnapshot(host, root) {
  if (!host.preOptimisticSortSnapshot) return;

  const headers = root.querySelectorAll("table th[aria-sort]");
  headers.forEach((header, index) => {
    const value = host.preOptimisticSortSnapshot[index];
    if (value != null) header.setAttribute("aria-sort", value);
  });
  delete host.preOptimisticSortSnapshot;
}

export function turboFrameHeader(headers) {
  return headers?.["Turbo-Frame"] || headers?.get?.("Turbo-Frame");
}

export function isPrefetchRequest(headers) {
  if (!headers) return false;

  const purpose =
    headers["X-Sec-Purpose"] ||
    headers.get?.("X-Sec-Purpose") ||
    headers["Sec-Purpose"] ||
    headers.get?.("Sec-Purpose");
  return purpose
    ?.toString()
    .split(",")
    .map((value) => value.trim())
    .includes("prefetch");
}

export function matchesTurboFrameFetch(event, frameIds) {
  const headers = event.detail?.fetchOptions?.headers;
  if (isPrefetchRequest(headers)) return false;

  const frameHeader = turboFrameHeader(headers);
  return frameIds.includes(frameHeader);
}

export function frameElements(host) {
  if (host.frameTargets?.length > 0) return host.frameTargets;
  if (host.hasFrameTarget) return [host.frameTarget];
  return [];
}

export function skeletonHtmlForFrame(host, frame) {
  if (host.skeletonTemplateTargets?.length > 1) {
    const section = frame.id.includes("-late") ? "late" : "early";
    const template = host.skeletonTemplateTargets.find(
      (candidate) => candidate.dataset.ccaiSection === section,
    );
    if (template) return template.innerHTML;
  }

  if (host.hasSkeletonTemplateTarget) return host.skeletonTemplateTarget.innerHTML;

  return "";
}

export function connectImmediateSortSync(host) {
  host.boundSortClick = (event) => handleSortClickCapture(host, event);
  host.element.addEventListener("click", host.boundSortClick, true);
}

export function disconnectImmediateSortSync(host) {
  if (!host.boundSortClick) return;

  host.element.removeEventListener("click", host.boundSortClick, true);
}

export function handleSortClickCapture(host, event) {
  const frames = frameElements(host);
  if (frames.length === 0) return;

  const link = event.target.closest(SORT_LINK_SELECTOR);
  if (!link) return;

  const frame = frames.find((candidate) => candidate.contains(link));
  if (!frame) return;

  const header = link.closest("th[aria-sort]");
  if (!header) return;

  const sortableColumnIndex = Array.from(
    header.parentElement.querySelectorAll("th[aria-sort]"),
  ).indexOf(header);
  if (sortableColumnIndex < 0) return;

  host.pendingSortFrame = frame;
  host.pendingSortColumnIndex = sortableColumnIndex;
  host.pendingTargetAriaSort = ariaSortForHref(link.href);
  captureSortSnapshot(host, frame);
  applySortToTables(
    frame,
    host.pendingSortColumnIndex,
    host.pendingTargetAriaSort,
  );
}

export function showTableSkeleton(host, frame = null) {
  if (!host.hasSkeletonTemplateTarget) return;

  const frames = frame ? [frame] : frameElements(host);
  if (frames.length === 0) return;

  frames.forEach((targetFrame) => {
    targetFrame.setAttribute("aria-busy", "true");
    targetFrame.innerHTML = skeletonHtmlForFrame(host, targetFrame);
    targetFrame.dispatchEvent(
      new CustomEvent(SKELETON_SHOWN_EVENT, { bubbles: false }),
    );

    if (
      host.pendingSortFrame === targetFrame &&
      host.pendingSortColumnIndex != null &&
      host.pendingTargetAriaSort != null
    ) {
      applySortToTables(
        targetFrame,
        host.pendingSortColumnIndex,
        host.pendingTargetAriaSort,
      );
    }
  });
}

export function clearPendingSort(host) {
  host.pendingSortFrame = null;
  host.pendingSortColumnIndex = null;
  host.pendingTargetAriaSort = null;
}

export function revertOptimisticSort(host, frame = null) {
  const targetFrame = frame || host.pendingSortFrame || host.frameTarget;
  if (!targetFrame) return;

  restoreSortSnapshot(host, targetFrame);
  clearPendingSort(host);
  targetFrame.setAttribute("aria-busy", "false");
}

export function markTableFrameLoaded(host, frame = null) {
  const targetFrame = frame || host.frameTarget;
  if (!targetFrame) return;

  targetFrame.setAttribute("aria-busy", "false");
  if (host.pendingSortFrame === targetFrame) {
    delete host.preOptimisticSortSnapshot;
    clearPendingSort(host);
  }
}
